import React, { memo } from 'react';
import { FaDesktop } from 'react-icons/fa';
import { FaThumbtack } from 'react-icons/fa6';

interface ShareChipProps {
  /** Who is sharing */
  name: string;

  /** Pin their screen */
  onPin: () => void;
}

/**
 * "<name> is sharing · Pin" in the full-screen top bar (issue #124, epic D10).
 *
 * A screen share pins itself only when nothing is pinned; while something
 * else is, it does not take the pin and this chip offers it instead, one
 * click away. It is a status line with a real "Pin" button inside, labelled
 * "Pin <name>'s screen".
 */
const ShareChipComponent: React.FC<ShareChipProps> = ({ name, onPin }) => {
  const label = `Pin ${name}'s screen`;

  return (
    <div
      role="status"
      data-testid="share-chip"
      className="flex h-9.5 min-w-0 items-center gap-2.5 whitespace-nowrap rounded-full border border-game-golden/55 bg-game-golden/12 pl-3 pr-1 text-sm text-game-cream"
    >
      <FaDesktop aria-hidden="true" className="shrink-0 text-game-golden" />
      <span className="min-w-0 truncate">
        <strong className="font-semibold text-white">{name}</strong> is sharing
      </span>
      <button
        type="button"
        onClick={onPin}
        aria-label={label}
        title={label}
        className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full bg-game-golden px-2.5 text-[13px] font-bold text-game-navy transition duration-200 motion-reduce:transition-none hover:shadow-lg hover:shadow-game-golden/20 hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-game-golden focus-visible:ring-offset-2 focus-visible:ring-offset-game-navy [@media(hover:none)]:h-9"
      >
        <FaThumbtack aria-hidden="true" className="text-xs" />
        <span>Pin</span>
      </button>
    </div>
  );
};

export const ShareChip = memo(ShareChipComponent);
ShareChip.displayName = 'ShareChip';
